import { useEffect, useRef, useState } from 'react';
import axios from '../utils/axios';
import { getErrorStatus } from '../utils/requestErrors';
import { normalizeLandingPageLayout, type LandingPageSection, type MarketLandingPageLayout } from '../landingPage/marketLandingPageLayout';

export type LandingPageRevision = {
    id: number;
    pageType: MarketLandingPageLayout['mode'];
    changedAt: string;
    changedByUserId: number;
    changedByUserEmail?: string | null;
    sections: LandingPageSection[];
    isCurrent: boolean;
};

export function useMarketLandingPageEditor(marketId: number) {
    const [layout, setLayout] = useState<MarketLandingPageLayout | null>(null);
    const [saved, setSaved] = useState<MarketLandingPageLayout | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [loadRequest, setLoadRequest] = useState(0);
    const [history, setHistory] = useState<LandingPageRevision[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [historyPage, setHistoryPage] = useState(0);
    const [historyRequest, setHistoryRequest] = useState(0);
    const activeMarketId = useRef(marketId);
    activeMarketId.current = marketId;
    const pageType = layout?.mode;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLayout(null);
        setSaved(null);
        setSaving(false);
        setError('');
        setNotice('');
        setHistoryPage(0);
        axios.get(`/market/${marketId}/landing-page-layout`).then(({ data }) => {
            if (cancelled) return;
            const next = normalizeLandingPageLayout(data);
            setLayout(next);
            setSaved(next);
        }).catch(() => {
            if (!cancelled) setError('Unable to load landing page settings.');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [marketId, loadRequest]);

    useEffect(() => {
        if (!pageType) return;
        let cancelled = false;
        setHistoryLoading(true);
        setHistoryError('');
        setHistory([]);
        axios.get(`/market/${marketId}/landing-page-layout/${pageType}/history`, { params: { page: historyPage } })
            .then(({ data }) => { if (!cancelled) setHistory(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setHistoryError('Unable to load historical versions.'); })
            .finally(() => { if (!cancelled) setHistoryLoading(false); });
        return () => { cancelled = true; };
    }, [marketId, pageType, historyPage, historyRequest]);

    async function save() {
        if (!layout) return;
        const requestMarketId = marketId;
        setSaving(true);
        setError('');
        setNotice('');
        try {
            const next = { ...layout, configuredPageTypes: [...new Set([...(layout.configuredPageTypes ?? []), layout.mode])] };
            const pageTypes: MarketLandingPageLayout['mode'][] = ['Standard', 'Regulatory'];
            const changedPageTypes = pageTypes.filter((type) => {
                const sectionKey = type === 'Regulatory' ? 'regulatory' : 'standard';
                return JSON.stringify(next[sectionKey]) !== JSON.stringify(saved?.[sectionKey])
                    || next.configuredPageTypes.includes(type) && !saved?.configuredPageTypes?.includes(type);
            });
            const { data } = await axios.put(`/market/${requestMarketId}/landing-page-layout`, { ...next, changedPageTypes });
            if (activeMarketId.current !== requestMarketId) return;
            const persisted = normalizeLandingPageLayout(data ?? next);
            setLayout(persisted);
            setSaved(persisted);
            setHistoryPage(0);
            setHistoryRequest((value) => value + 1);
            setNotice('Landing page settings saved.');
        } catch (saveError) {
            if (activeMarketId.current !== requestMarketId) return;
            const status = getErrorStatus(saveError);
            setError(status === 409
                ? 'These settings were changed by another editor. Your draft is preserved; reload the page to review the latest saved settings.'
                : 'Unable to save landing page settings.');
        } finally {
            if (activeMarketId.current === requestMarketId) setSaving(false);
        }
    }

    async function restore(revision: LandingPageRevision) {
        if (!window.confirm(`Restore version #${revision.id} as a new save? Unsaved changes will be discarded.`)) return;
        const requestMarketId = marketId;
        setSaving(true);
        setError('');
        setNotice('');
        try {
            const { data } = await axios.post(`/market/${requestMarketId}/landing-page-layout/${revision.pageType}/history/${revision.id}/restore`);
            if (activeMarketId.current !== requestMarketId) return;
            const restored = normalizeLandingPageLayout(data);
            setLayout(restored);
            setSaved(restored);
            setHistoryPage(0);
            setHistoryRequest((value) => value + 1);
            setNotice(`Version #${revision.id} restored as a new save.`);
        } catch {
            if (activeMarketId.current === requestMarketId) setError('Unable to restore historical version.');
        } finally {
            if (activeMarketId.current === requestMarketId) setSaving(false);
        }
    }

    return {
        layout, setLayout, saved, loading, saving, error, setError, notice, setNotice,
        history, historyLoading, historyError, historyPage, setHistoryPage,
        retryLoad: () => setLoadRequest((value) => value + 1),
        retryHistory: () => setHistoryRequest((value) => value + 1),
        save, restore,
    };
}
